/* eslint-disable @typescript-eslint/no-explicit-any */
// TODO: This file was automatically migrated to TypeScript by `dev typescript migrate`.
//  The eslint disable comment above should be removed and the resulting errors fixed.
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {ReactNode} from 'react';
import isEqual from 'react-fast-compare';
import _ from 'underscore';

import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import {NucleotideType} from 'biojs/BioConstants';
import {
  CRISPRAnalysisSequenceAttributes,
  CRISPRGuide,
  GuideGroupSort,
  SORT_KEY,
} from 'constants/CRISPRConstants';
import {CRISPRAnalysis} from 'constants/CRISPRConstants';
import * as sequtil from 'molbio/SeqUtil';
import notifications from 'notifications';
import EntityStore from 'stores/EntityStore';
import getTestProps from 'test/lib/cypress/getTestProps';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {BLUE, BLUE_LIGHT} from 'ui-library/Colors';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Popover from 'ui-library/floating-ui/Popover';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import IndeterminateCheckbox from 'ui-library/IndeterminateCheckbox';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import runModal from 'ui-library/runModal';
import copyToClipboard from 'util/clipboard/copyToClipboard';
import * as logging from 'util/Logging';
import {capitalize} from 'util/NameUtil';
import pick from 'util/pick';

// @ts-expect-error
import CRISPRAnalysisActions from './CRISPRAnalysisActions';
import CRISPRAnalysisStore from './CRISPRAnalysisStore';
import * as CRISPRUtil from './CRISPRUtil';
import GuidePrimerModal from './GuidePrimerModal';

const PAGE_SIZE = 100;

export interface GuideGroupTableHelpOptions {
  helpClassName?: string;
  content: ReactNode;
  title?: string;
}

export interface GuideGroupTableColumnSpec {
  header: string;
  extraHeader?: string;
  sortKey?: string;
  help?: GuideGroupTableHelpOptions;
}

interface Props {
  analysis: CRISPRAnalysis;
  guideGroups: Array<Array<CRISPRGuide>>;
  guideGroupSize: number;
  openAssemblyTab: (...args: Array<any>) => any;
  selection?: {
    start?: number;
    end?: number | null;
  };
  sequence: CRISPRAnalysisSequenceAttributes;
  sequencePrimers: Array<{
    start: number;
    end: number;
    strand: number;
  }>;
  setSelection: (...args: Array<any>) => any;
  saveGuidePrimers: (...args: Array<any>) => any;
  showGuidePrimers: (...args: Array<any>) => any;
  guideGroupSort: GuideGroupSort;
  selectedGuideGroups: any;
  guideGroupComponentsMap: any;
  guidesToTSV: (...args: Array<any>) => any;
  columns: Array<GuideGroupTableColumnSpec>;
  tableClassName?: string;
  getSortIteratee: (...args: Array<any>) => any;
  getGuidePosition: (...args: Array<any>) => any;
}

class GuideGroupTable extends React.Component<Props> {
  componentDidUpdate(prevProps: Props): void {
    if (!isEqual(prevProps.selectedGuideGroups, this.props.selectedGuideGroups)) {
      this.props.showGuidePrimers(this.getSelectedGuides());
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type,@typescript-eslint/explicit-module-boundary-types
  getSelectedGuides = () =>
    // eslint-disable-next-line no-restricted-syntax
    _.flatten(
      this.getSortedGuideGroups(this.props.guideGroups.filter((group) => this.isGuideGroupSelected(group))),
      true
    );

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type,@typescript-eslint/explicit-module-boundary-types
  getSortedGuideGroups = (guideGroups: any) => {
    const sortedGuideGroups = _.chain(guideGroups)
      .sortBy(this.groupPosition)
      .sortBy(this.props.getSortIteratee())
      .value();
    if (this.props.guideGroupSort.reverse) {
      sortedGuideGroups.reverse();
    }
    return sortedGuideGroups;
  };

  groupPosition = (guideGroup: any): number =>
    Math.min(...guideGroup.map((group: any) => this.props.getGuidePosition(group)));

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type,@typescript-eslint/explicit-module-boundary-types
  selectedGuidesToTSV = () => this.props.guidesToTSV(this.getSelectedGuides());

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type,@typescript-eslint/explicit-module-boundary-types
  allGuidesToTSV = () =>
    // eslint-disable-next-line no-restricted-syntax
    this.props.guidesToTSV(_.flatten(this.getSortedGuideGroups(this.props.guideGroups), true));

  notifyGuidesTSVCopied = (which: any): void => {
    notifications.success(
      `${capitalize(which)} guides have been copied in tab-delimited format,
      which can be pasted into Excel.`,
      {duration: 6, id: `${which}-guides-copied`}
    );
  };

  isGuideGroupSelected = (guideGroup: any): boolean =>
    CRISPRAnalysisStore.isGuideGroupSelected(this.props.selectedGuideGroups, guideGroup);

  selectGuideGroups = (guideGroups: any, e: any): void => {
    CRISPRAnalysisActions.toggleSelectedGuideGroups(this.props.analysis.id, guideGroups, e.target.checked);
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type,@typescript-eslint/explicit-module-boundary-types
  sortGuideGroups = (sortKey: any) => {
    if (this.props.guideGroupSort.key === sortKey) {
      CRISPRAnalysisActions.updateGuideGroupSort(this.props.analysis.id, {
        reverse: !this.props.guideGroupSort.reverse,
      });
    } else {
      CRISPRAnalysisActions.updateGuideGroupSort(this.props.analysis.id, {
        key: sortKey,
        reverse:
          [SORT_KEY.SPECIFICITY_SINGLE, SORT_KEY.SPECIFICITY_PAIR, SORT_KEY.EFFICIENCY].indexOf(sortKey) !==
          -1,
      });
    }
  };

  guideToPrimer = (guide: any): {end: number; start: number; strand: any} => ({
    start: CRISPRUtil.guideStart(guide, this.props.sequence.length, this.props.analysis.isLeftPam),
    end: CRISPRUtil.guideEnd(guide, this.props.sequence.length, this.props.analysis.isLeftPam),
    strand: guide.strand,
  });

  guideGroupSelectionRange = (guideGroup: any): {end: number; start: number} => {
    if (guideGroup.length === 1) {
      return pick(this.guideToPrimer(guideGroup[0]), 'start', 'end');
    }
    assert(guideGroup.length === 2, 'expected guideGroup to have length 2');

    const sortedGuides = _.min([guideGroup, [guideGroup[1], guideGroup[0]]], ([g1, g2]) => {
      const start = CRISPRUtil.guideStart(g1, this.props.sequence.length, this.props.analysis.isLeftPam);

      // If the sequence is linear, ensure the earlier guide start is first
      if (!this.props.sequence.circular) {
        return start;
      }

      const end = CRISPRUtil.guideEnd(g2, this.props.sequence.length, this.props.analysis.isLeftPam);
      // If the sequence is circular, the selection could wrap around the start of the sequence
      // We want to make sure that the selection covers both guides so we want to take the larger of the two
      // selections.
      return -1 * sequtil.distance(start, end, this.props.sequence.length, true);
    });

    if (!sortedGuides[1] || !sortedGuides[0]) {
      logging.error('Forward or reverse guide is undefined in paired guide table', {extra: {guideGroup}});
    }
    return {
      start: CRISPRUtil.guideStart(
        sortedGuides[0],
        this.props.sequence.length,
        this.props.analysis.isLeftPam
      ),
      end: CRISPRUtil.guideEnd(sortedGuides[1], this.props.sequence.length, this.props.analysis.isLeftPam),
    };
  };

  guideGroupClickHandler = (guideGroup: any, e: any): void => {
    const {start, end} = this.guideGroupSelectionRange(guideGroup);
    this.props.setSelection(start, end);
  };

  analyticsTrackSavedGuides = (numGuides: any): void => {
    analytics.track('crispr', 'saved_guides', {
      crispr_analysis_id: this.props.analysis.id,
      nucleotide_type: NucleotideType.DNA,
      num_guides: numGuides,
      save_destination: 'clipboard',
      sequence_id: this.props.sequence.id,
    });
  };

  isGuideGroupActive = (guideGroup: any): boolean =>
    isEqual(this.guideGroupSelectionRange(guideGroup), this.props.selection);

  isSavedPrimer = (guideGroup: any): boolean =>
    guideGroup.every((guide: any) =>
      Boolean(_.findWhere(this.props.sequencePrimers, this.guideToPrimer(guide)))
    );

  // eslint-disable-next-line no-restricted-syntax
  getNumSelectedGuides = (): number => _.size(this.props.selectedGuideGroups) * this.props.guideGroupSize;

  runGuidePrimerModal = async (): Promise<void> => {
    const modalProps = {
      numSelectedGuides: this.getNumSelectedGuides(),
      designType: this.props.analysis.designType,
      saveGuidePrimers: (folderId: string, nucleotideType: NucleotideType, tagSchemaId: string | undefined) =>
        this.props.saveGuidePrimers(folderId, nucleotideType, tagSchemaId).done(() => {
          analytics.track('crispr', 'saved_guides', {
            crispr_analysis_id: this.props.analysis.id,
            nucleotide_type: nucleotideType,
            num_guides: this.getNumSelectedGuides(),
            save_destination: 'benchling',
            sequence_id: this.props.sequence.id,
          });
        }),
    };
    const success = await runModal(GuidePrimerModal, modalProps);
    if (success) {
      CRISPRAnalysisActions.toggleSelectedGuideGroups(this.props.analysis.id, this.props.guideGroups, false);
    }
  };

  renderColumnNode = (
    sortKey: string,
    header: string,
    extraHeader: string,
    help?: GuideGroupTableHelpOptions
  ): JSX.Element => {
    const baseLinkClassNames: Record<string, boolean> = {
      [css(styles.headerCell)]: true,
      [css(styles.sortable)]: sortKey != null,
      'u-no-text-decoration': true,
      columnNodeHeader: true,
      'u-dotted-underline': Boolean(help),
    };

    const className = help
      ? classNames(help.helpClassName, baseLinkClassNames)
      : classNames(baseLinkClassNames);

    const handleOnClick = (): void => {
      if (sortKey) {
        this.sortGuideGroups(sortKey);
      }
    };
    return (
      <span className={className} onClick={handleOnClick}>
        {header}
        {(() => {
          if (extraHeader) {
            return (
              <span>
                <br />
                {extraHeader}
              </span>
            );
          }
          return null;
        })()}
        {(() => {
          if (this.props.guideGroupSort.key === sortKey) {
            return (
              <span>
                {'\u00A0'}
                <BenchlingIcon icon={this.props.guideGroupSort.reverse ? 'chevron-down' : 'chevron-up'} />
              </span>
            );
          }
          return null;
        })()}
      </span>
    );
  };

  renderColumnNodeWithHelp = (
    sortKey: string,
    header: string,
    extraHeader: string,
    help: GuideGroupTableHelpOptions
  ): JSX.Element => (
    <Popover
      placement={FloatingPlacement.TOP}
      renderContent={() => <div className={help.helpClassName}>{help.content}</div>}
      title={help.title}
    >
      {this.renderColumnNode(sortKey, header, extraHeader, help)}
    </Popover>
  );

  renderColumnHeader = (key: any, sortKey: any, header: any, extraHeader: any, help: any): JSX.Element => (
    <th className="u-no-select text-left" key={key}>
      <span>
        {(() => {
          if (help) {
            return this.renderColumnNodeWithHelp(sortKey, header, extraHeader, help);
          }
          return this.renderColumnNode(sortKey, header, extraHeader);
        })()}
      </span>
    </th>
  );

  renderEmptyTable = (): JSX.Element => (
    <tbody>
      <tr className="faded">
        <td colSpan={6}>No guides found in target regions.</td>
      </tr>
    </tbody>
  );

  renderSingleGuidesTable = (paginatedGuideGroups: any): JSX.Element => (
    <tbody {...getTestProps({component: 'GuideGroupTable', element: 'root', key: 'single'})}>
      {paginatedGuideGroups.map((guideGroup: any) => {
        const guideGroupKey = CRISPRAnalysisStore.guideGroupKey(guideGroup);
        return this.props.guideGroupComponentsMap[guideGroupKey].map((row: any) => (
          <tr
            key={`${guideGroupKey}`}
            className={classNames({
              'crispr-guidesTable-row': true,
              active: this.isGuideGroupActive(guideGroup),
              success: this.isSavedPrimer(guideGroup),
            })}
            onClick={(e) => {
              this.guideGroupClickHandler(guideGroup, e);
            }}
            {...getTestProps({component: 'GuideGroupTable', element: 'row', key: 'single'})}
          >
            <td rowSpan={this.props.guideGroupSize} key="checkbox">
              <input
                type="checkbox"
                className={css(styles.checkbox)}
                checked={this.isGuideGroupSelected(guideGroup)}
                onChange={(e) => {
                  this.selectGuideGroups([guideGroup], e);
                }}
              />
            </td>
            {row}
          </tr>
        ));
      })}
    </tbody>
  );

  renderMultipleGuidesTable = (paginatedGuideGroups: any): React.ReactNode =>
    paginatedGuideGroups.map((guideGroup: any) => {
      const guideGroupKey = CRISPRAnalysisStore.guideGroupKey(guideGroup);
      return (
        <tbody
          className="crispr-guidesTable-multiple"
          key={guideGroupKey}
          {...getTestProps({component: 'GuideGroupTable', element: 'root', key: 'multiple'})}
        >
          {(() =>
            this.props.guideGroupComponentsMap[guideGroupKey].map((row: any, i: any) => (
              <tr
                key={`${guideGroupKey}-${i}`}
                className={classNames({
                  'crispr-guidesTable-row': true,
                  active: this.isGuideGroupActive(guideGroup),
                  success: this.isSavedPrimer(guideGroup),
                })}
                onClick={(e) => {
                  this.guideGroupClickHandler(guideGroup, e);
                }}
                {...getTestProps({component: 'GuideGroupTable', element: 'row', key: 'multiple'})}
              >
                {(() => {
                  if (i === 0) {
                    return (
                      <td rowSpan={this.props.guideGroupSize} key="checkbox">
                        <input
                          type="checkbox"
                          checked={this.isGuideGroupSelected(guideGroup)}
                          onChange={(e) => {
                            this.selectGuideGroups([guideGroup], e);
                          }}
                        />
                      </td>
                    );
                  }
                  return null;
                })()}
                {row}
              </tr>
            )))()}
        </tbody>
      );
    });

  render(): JSX.Element {
    const sortedGuideGroups = this.getSortedGuideGroups(this.props.guideGroups);
    const pageSize = Math.floor(PAGE_SIZE / this.props.guideGroupSize);
    const guideGroupStartIndex = this.props.guideGroupSort.offset;
    const guideGroupEndIndex = Math.min(
      this.props.guideGroupSort.offset + pageSize,
      this.props.guideGroups.length
    );
    // [guideGroupStartIndex, guideGroupEndIndex) defines the current page.
    const paginatedGuideGroups = sortedGuideGroups.slice(guideGroupStartIndex, guideGroupEndIndex);
    const prevGuideGroupIndex = guideGroupStartIndex - pageSize;
    const nextGuideGroupIndex = guideGroupStartIndex + pageSize;
    const disablePrev = prevGuideGroupIndex < 0;
    const disableNext = nextGuideGroupIndex >= this.props.guideGroups.length;

    const copySelectedToTSV = (): void => {
      const data = this.selectedGuidesToTSV();
      copyToClipboard(data, {showToastOnSuccess: false});
      this.notifyGuidesTSVCopied('selected');
      this.analyticsTrackSavedGuides(this.getNumSelectedGuides());
    };

    const copyAllToTSV = (): void => {
      const data = this.allGuidesToTSV();
      copyToClipboard(data, {showToastOnSuccess: false});
      this.notifyGuidesTSVCopied('all');
      this.analyticsTrackSavedGuides(this.props.guideGroups.length);
    };

    const isUserLoggedIn = EntityStore.currentUser() != null;
    return (
      <div>
        <Row>
          <Col sm={6}>
            {
              // @ts-expect-error TODO: guidesToTSV appears non-null, so a null check shouldn't be necessary.
              this.props.guidesToTSV && (
                <Dropdown
                  menu={
                    <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.LARGE}>
                      <Option isDisabled={this.getNumSelectedGuides() === 0} onSelect={copySelectedToTSV}>
                        Export selected (.tsv)
                      </Option>
                      <Option onSelect={copyAllToTSV}>Export all (.tsv)</Option>
                    </Menu>
                  }
                >
                  <Button
                    className="u-tiny-spacing-right"
                    isDropdown={true}
                    buttonStyle={ButtonStyle.TERTIARY_GRAY}
                    size={ButtonSize.SMALL}
                    text="Export"
                  />
                </Dropdown>
              )
            }
            {isUserLoggedIn && (
              <>
                <Button
                  buttonStyle={ButtonStyle.SECONDARY}
                  size={ButtonSize.SMALL}
                  className="u-tiny-spacing-right"
                  type="button"
                  disabled={this.getNumSelectedGuides() === 0}
                  onClick={() => this.runGuidePrimerModal()}
                  text="Save"
                />
                <Button
                  buttonStyle={ButtonStyle.PRIMARY}
                  size={ButtonSize.SMALL}
                  className="u-tiny-spacing-right"
                  type="button"
                  disabled={this.getNumSelectedGuides() === 0}
                  onClick={() => this.props.openAssemblyTab(this.props.analysis, this.getSelectedGuides())}
                  text="Assemble"
                />
              </>
            )}
            <span className="faded u-input-line-height"> {`${this.getNumSelectedGuides()} selected`} </span>
          </Col>
          <Col className="text-right" sm={6}>
            <span className="faded u-input-line-height u-small-spacing-right">
              {`${guideGroupStartIndex * this.props.guideGroupSize + 1}-` +
                `${guideGroupEndIndex * this.props.guideGroupSize} of ` +
                `${this.props.guideGroups.length * this.props.guideGroupSize}`}
            </span>
            <div className="resultsPager u-input-line-height">
              <ul className="pager">
                <li className={classNames({disabled: disablePrev, 'u-tiny-spacing-right': true})}>
                  {(() => {
                    if (disablePrev) {
                      return <span>Prev</span>;
                    }
                    return (
                      <a
                        onClick={() => {
                          CRISPRAnalysisActions.updateGuideGroupSort(this.props.analysis.id, {
                            offset: prevGuideGroupIndex,
                          });
                        }}
                      >
                        Prev
                      </a>
                    );
                  })()}
                </li>
                <li className={classNames({disabled: disableNext})}>
                  {(() => {
                    if (disableNext) {
                      return <span>Next</span>;
                    }
                    return (
                      <a
                        onClick={() => {
                          CRISPRAnalysisActions.updateGuideGroupSort(this.props.analysis.id, {
                            offset: nextGuideGroupIndex,
                          });
                        }}
                      >
                        Next
                      </a>
                    );
                  })()}
                </li>
              </ul>
            </div>
          </Col>
        </Row>
        <table className={classNames('table', this.props.tableClassName)}>
          <thead>
            <tr>
              <th key="checkbox">
                <IndeterminateCheckbox
                  items={paginatedGuideGroups}
                  selectedMap={this.isGuideGroupSelected}
                  onChange={(e) => {
                    this.selectGuideGroups(paginatedGuideGroups, e);
                  }}
                />
              </th>
              {(() =>
                this.props.columns.map((column) => {
                  const key = column.sortKey || `column-${column.header}`;
                  return this.renderColumnHeader(
                    key,
                    column.sortKey,
                    column.header,
                    column.extraHeader,
                    column.help
                  );
                }))()}
            </tr>
          </thead>
          {(() => {
            if (paginatedGuideGroups.length === 0) {
              return this.renderEmptyTable();
            } else if (this.props.guideGroupSize === 1) {
              return this.renderSingleGuidesTable(paginatedGuideGroups);
            }
            return this.renderMultipleGuidesTable(paginatedGuideGroups);
          })()}
        </table>
      </div>
    );
  }
}

export default GuideGroupTable;

const styles = StyleSheet.create({
  headerCell: {
    color: BLUE_LIGHT,
    ':hover': {
      color: BLUE,
    },
  },
  sortable: {cursor: 'pointer'},
  checkbox: {
    accentColor: BLUE,
  },
});
